import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Box, Button, Typography, CircularProgress } from "@mui/material";
import ClusterDenLogo from "../../Images/Login/ClusterDenLogo.png";
import ClusterDenLogoWhite from "../../Images/Login/ClusterDenLogoWhite.png";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useThemeContext } from "../../contexts/ThemeContext";
import { verifyEmailAndLogin } from "../../redux/Slice/Auth/authSlice";

const RESEND_COOLDOWN_SECONDS = 30;

const VerifyEmail = () => {
  const { token } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { mode } = useThemeContext();
  const apiCalled = useRef(false);
  const cooldownIntervalRef = useRef(null);

  const [status, setStatus] = useState("verifying"); // verifying | success | alreadyVerified | expired | error
  const [message, setMessage] = useState("Verifying your email…");
  const [emailForResend, setEmailForResend] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendMessage, setResendMessage] = useState("");
  const [resending, setResending] = useState(false);

  const accentColor = mode === "dark" ? "#4FC389" : "#44B57C";

  useEffect(() => {
    if (apiCalled.current) return;
    apiCalled.current = true;

    const run = async () => {
      const result = await dispatch(verifyEmailAndLogin(token));

      if (result.meta.requestStatus === "fulfilled") {
        const payload = result.payload;
        if (payload.accessToken) {
          setStatus("success");
          setMessage("Email verified! Redirecting…");
          const destination = payload.requiresOnboarding ? "/setup" : `/home/${payload.accountId}`;
          setTimeout(() => navigate(destination, { replace: true }), 800);
        } else {
          setStatus("alreadyVerified");
          setMessage(payload.message || "This email is already verified. Please log in.");
        }
        return;
      }

      const errorData = result.payload || {};
      if (errorData.expired) {
        setStatus("expired");
        setMessage(errorData.message || "This verification link has expired.");
        setEmailForResend(errorData.email || "");
      } else {
        setStatus("error");
        setMessage(errorData.message || "Unable to verify this link. Please try again or request a new one.");
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    return () => {
      if (cooldownIntervalRef.current) clearInterval(cooldownIntervalRef.current);
    };
  }, []);

  const startResendCooldown = () => {
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    if (cooldownIntervalRef.current) clearInterval(cooldownIntervalRef.current);
    cooldownIntervalRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResend = async () => {
    if (!emailForResend || resendCooldown > 0 || resending) return;
    setResending(true);
    setResendMessage("");
    try {
      const apiUrl = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";
      const response = await fetch(`${apiUrl}/api/accounts/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: emailForResend }),
      });
      const data = await response.json();
      setResendMessage(data.message || (data.success ? "Verification email sent." : "Unable to resend right now."));
      if (data.success || response.status === 429) {
        startResendCooldown();
      }
    } catch (err) {
      setResendMessage("Unable to resend right now. Please try again.");
    } finally {
      setResending(false);
    }
  };

  const logoBoxSx = {
    display: "flex",
    alignItems: "center",
  };

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: mode === "dark" ? "#121212" : "#FFFFFF",
        fontFamily: "Figtree, sans-serif",
        gap: 3,
        px: 2,
      }}
    >
      <Box sx={logoBoxSx}>
        <Box
          component="img"
          src={mode === "dark" ? ClusterDenLogoWhite : ClusterDenLogo}
          alt="ClusterDen"
          sx={{ height: 40, width: "auto" }}
        />
      </Box>

      {status === "verifying" && <CircularProgress size={48} thickness={3} sx={{ color: accentColor }} />}

      {status === "success" && (
        <CheckCircleOutlineIcon sx={{ fontSize: 48, color: accentColor }} />
      )}

      {(status === "expired" || status === "error") && (
        <ErrorOutlineIcon sx={{ fontSize: 48, color: mode === "dark" ? "#f87171" : "#EF4444" }} />
      )}

      {status === "alreadyVerified" && (
        <CheckCircleOutlineIcon sx={{ fontSize: 48, color: accentColor }} />
      )}

      <Typography
        sx={{
          fontFamily: "Figtree, sans-serif",
          fontWeight: 700,
          fontSize: "1.3rem",
          color: mode === "dark" ? "#f5f5f5" : "#121a34",
          textAlign: "center",
          maxWidth: 420,
        }}
      >
        {message}
      </Typography>

      {status === "expired" && emailForResend && (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
          {resendMessage && (
            <Typography
              sx={{
                fontSize: "13px",
                color: mode === "dark" ? "#9ca3af" : "#6b7280",
                fontFamily: "Figtree, sans-serif",
              }}
            >
              {resendMessage}
            </Typography>
          )}
          <Button
            onClick={handleResend}
            disabled={resendCooldown > 0 || resending}
            variant="contained"
            sx={{
              py: 1.5,
              px: 4,
              backgroundColor: accentColor,
              color: "#fff",
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 600,
              fontFamily: "Figtree, sans-serif",
              "&:hover": { backgroundColor: mode === "dark" ? "#3aad77" : "#38a06c" },
            }}
          >
            {resending
              ? "Sending…"
              : resendCooldown > 0
              ? `Resend email (${resendCooldown}s)`
              : "Resend verification email"}
          </Button>
        </Box>
      )}

      {(status === "alreadyVerified" || status === "error") && (
        <Button
          onClick={() => navigate("/")}
          variant="contained"
          sx={{
            py: 1.5,
            px: 4,
            backgroundColor: accentColor,
            color: "#fff",
            borderRadius: "12px",
            textTransform: "none",
            fontWeight: 600,
            fontFamily: "Figtree, sans-serif",
            "&:hover": { backgroundColor: mode === "dark" ? "#3aad77" : "#38a06c" },
          }}
        >
          Go to Login
        </Button>
      )}
    </Box>
  );
};

export default VerifyEmail;
